import type { CSSProperties, ReactNode, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { mergeProps, useButton, useFocusRing, useTableCell } from 'react-aria';
import type { AriaButtonProps } from 'react-aria';

import { clsx } from '../../../../lib/clsx.ts';
import { HomeyApiKind } from '../../../../lib/HomeyApiKind';
import { SCOPES } from '../../../../lib/scopes';
import { ToastManager } from '../../../../ToastManager.jsx';
import { DeviceSettingsFirmwareUpdateWakeDialog } from '../../../devices/settings/DeviceSettingsFirmwareUpdateWakeDialog';
import { useCellContext } from '../SettingsTable';

import type { V3DeviceProps } from '../../../../store/devices/DevicesStore.ts';

import { useI18n } from '../../../../hooks/useI18nFormatters';
import { useDevice } from '../../../../store/devices/useDevices.ts';
import { useApi } from '../../../../store/SelectedHomeyStore.hooks';

import { glyph } from '../../../../theme/glyph';
import { theme } from '../../../../theme/theme';
import styles from './DeviceUpdatesSettingsTableCell.module.css';

import { Glyph } from '../../../../components/common/Glyph';
import { Tooltip } from '../../../../components/common/tooltip/Tooltip';
import { DeviceIcon } from '../../../../components/device/DeviceIcon';

import { DeviceUpdatesPromptDialog } from './DeviceUpdatesPromptDialog';
import { useDeviceUpdatesSettingsTableRowContext } from './DeviceUpdatesSettingsTableRow';

interface DeviceUpdatesTableCellProps {
  type: 'in-progress' | 'available';
  onVersionPress?: () => void;
}

interface DeviceUpdatesTableButtonProps extends AriaButtonProps<'button'> {
  children: ReactNode;
  className: string;
  title?: string;
}

interface FirmwareDetailsResult {
  changelog?: string | null;
  changelogUrl?: string | null;
  wakeInstruction?: string | null;
}

interface FirmwareCheckResult extends FirmwareDetailsResult {
  availableVersion?: string | null;
  currentVersion?: string | null;
}

export function DeviceUpdatesSettingsTableCell(props: DeviceUpdatesTableCellProps) {
  const { i18n } = useI18n();
  const { cell, state, row } = useCellContext<V3DeviceProps>();

  const ref = useRef(null);
  const tableCell = useTableCell({ node: cell }, state, ref);
  const focusRing = useFocusRing();
  const columnKey = cell.column?.key;

  const { device } = useDevice({
    deviceId: String(row.key),
    apiKinds: [HomeyApiKind.V3_LOCAL, HomeyApiKind.V3_CLOUD],
  });
  const { progress: rowProgress } = useDeviceUpdatesSettingsTableRowContext();
  const { api, scopes } = useApi();
  const firmwareUpdate = device?.firmwareUpdate ?? row.value?.firmwareUpdate ?? null;
  const progress = rowProgress ?? firmwareUpdate?.progress ?? null;
  const [isPromptOpen, setPromptOpen] = useState(false);
  const [isWakeDialogOpen, setWakeDialogOpen] = useState(false);
  const [isStartingUpdate, setStartingUpdate] = useState(false);
  const [pendingUpdateVersion, setPendingUpdateVersion] = useState<string | null>(null);
  const [wakeInstruction, setWakeInstruction] = useState<string | null>(null);

  const deviceHasAccentColor = device?.color != null;
  const deviceIconWrapperStyle = {
    '--device-icon-bg': device?.color ?? theme.color.line,
  } as CSSProperties;

  function checkDeviceReady() {
    const device = useDevice.store.get().byId?.[String(row.key)];

    if (device?.ready !== true || device.available === false) {
      ToastManager.addError({
        message: i18n.messageFormatter('settings.system.deviceUpdatesDeviceNotReady'),
      });
      return false;
    }

    return true;
  }

  async function getAvailableVersion(deviceId: string) {
    const availableVersion = getString(firmwareUpdate?.availableVersion);

    if (availableVersion != null) {
      return availableVersion;
    }

    if (api == null) {
      return null;
    }

    const result = await api.call<FirmwareCheckResult>({
      method: 'POST',
      path: `/api/manager/devices/device/${deviceId}/firmware/check`,
      body: {},
      $timeout: 30000,
    });
    const checkedAvailableVersion = getString(result.availableVersion);
    const checkedWakeInstruction = getString(result.wakeInstruction);

    if (checkedWakeInstruction != null) {
      setWakeInstruction(checkedWakeInstruction);
    }

    if (checkedAvailableVersion == null) {
      ToastManager.add({
        type: ToastManager.TYPES.SUCCESS,
        icon: glyph.core.checkmarkCircleFill,
        iconColor: theme.color.white,
        i18n: null,
        message: i18n.messageFormatter('settings.system.deviceUpdatesDeviceUpToDate', {
          version:
            getString(result.currentVersion) ?? getString(firmwareUpdate?.currentVersion) ?? '-',
        }),
      });
      return null;
    }

    return checkedAvailableVersion;
  }

  async function loadWakeInstruction(deviceId: string, availableVersion: string) {
    if (api == null || scopes[SCOPES.UPDATES_READONLY] !== true) {
      return null;
    }

    try {
      const params = new URLSearchParams({ version: availableVersion });
      const result = await api.call<FirmwareDetailsResult>({
        method: 'GET',
        path: `/api/manager/devices/device/${deviceId}/firmware/details?${params.toString()}`,
        $timeout: 30000,
      });

      return getString(result.wakeInstruction);
    } catch (error: unknown) {
      ToastManager.handleError(error);
      return null;
    }
  }

  async function startFirmwareUpdate(deviceId: string, availableVersion: string) {
    if (api == null || checkDeviceReady() === false) {
      return false;
    }

    await api.call({
      method: 'POST',
      path: `/api/manager/devices/device/${deviceId}/firmware/start`,
      body: { version: availableVersion },
      $timeout: 30000,
    });

    return true;
  }

  async function handlePromptStartUpdate() {
    const deviceId = row.value?.id;

    if (
      api == null ||
      deviceId == null ||
      scopes[SCOPES.UPDATES] !== true ||
      isStartingUpdate === true
    ) {
      return;
    }

    if (checkDeviceReady() === false) {
      return;
    }

    setStartingUpdate(true);

    try {
      const availableVersion = await getAvailableVersion(deviceId);

      if (availableVersion == null) {
        setPromptOpen(false);
        return;
      }

      if (firmwareUpdate?.requiresWake === true) {
        const nextWakeInstruction =
          wakeInstruction ?? (await loadWakeInstruction(deviceId, availableVersion));

        setWakeInstruction(nextWakeInstruction);
        setPendingUpdateVersion(availableVersion);
        setPromptOpen(false);
        setWakeDialogOpen(true);
        return;
      }

      const didStart = await startFirmwareUpdate(deviceId, availableVersion);

      if (didStart === false) {
        return;
      }

      setPromptOpen(false);
    } catch (error: unknown) {
      ToastManager.handleError(error);
    } finally {
      setStartingUpdate(false);
    }
  }

  async function handleWakeConfirm() {
    const deviceId = row.value?.id;

    if (
      api == null ||
      deviceId == null ||
      pendingUpdateVersion == null ||
      scopes[SCOPES.UPDATES] !== true ||
      isStartingUpdate === true
    ) {
      return;
    }

    setStartingUpdate(true);

    try {
      const didStart = await startFirmwareUpdate(deviceId, pendingUpdateVersion);

      if (didStart === false) {
        return;
      }

      setPendingUpdateVersion(null);
      setWakeDialogOpen(false);
    } catch (error: unknown) {
      ToastManager.handleError(error);
    } finally {
      setStartingUpdate(false);
    }
  }

  function getContent() {
    switch (columnKey) {
      case 'device': {
        return (
          <div className={styles.nameCell} data-type={columnKey}>
            <div className={styles.deviceIconWrapper} style={deviceIconWrapperStyle}>
              <DeviceIcon
                iconObj={device?.iconObj}
                iconUrl={null}
                iconOverride={device?.iconOverride ?? null}
                size="18px"
                color={
                  deviceHasAccentColor
                    ? (theme.color.white as typeof theme.color.icon_dark)
                    : undefined
                }
              />
            </div>

            <div className={styles.name} title={cell.textValue}>
              {cell.textValue}
            </div>
          </div>
        );
      }

      case 'zone': {
        return (
          <div className={styles.nameCell} data-type={columnKey}>
            <div className={styles.name} title={cell.textValue}>
              {cell.textValue}
            </div>
          </div>
        );
      }

      case 'version': {
        if (firmwareUpdate?.state === 'error') {
          return renderErrorState(columnKey);
        }

        return (
          <div className={styles.nameCell} data-type={columnKey}>
            {props.onVersionPress != null ? (
              <DeviceUpdatesTableButton
                className={styles.versionButton}
                title={cell.textValue}
                onPress={() => {
                  props.onVersionPress?.();
                }}
              >
                {cell.textValue}
              </DeviceUpdatesTableButton>
            ) : (
              <div className={styles.name} title={cell.textValue}>
                {cell.textValue}
              </div>
            )}
          </div>
        );
      }

      case 'state': {
        if (firmwareUpdate?.state === 'error') {
          return renderErrorState(columnKey);
        }

        const firmwareUpdateState = firmwareUpdate?.state ?? row.value?.firmwareUpdate?.state;
        const firmwareUpdateMessage =
          firmwareUpdate?.message ?? row.value?.firmwareUpdate?.message ?? null;

        return (
          <div className={styles.nameCell} data-type={columnKey}>
            <div
              className={styles.stateLabel}
              title={getStateLabel(
                firmwareUpdateState ?? null,
                firmwareUpdateMessage,
                cell.textValue,
                {
                  queued: i18n.messageFormatter('settings.system.deviceUpdatesStateQueued'),
                  installingFirmware: i18n.messageFormatter(
                    'settings.system.deviceUpdatesStateInstallingFirmware',
                  ),
                  abortingUpdate: i18n.messageFormatter(
                    'settings.system.deviceUpdatesStateAbortingUpdate',
                  ),
                },
              )}
            >
              {getStateLabel(firmwareUpdateState ?? null, firmwareUpdateMessage, cell.textValue, {
                queued: i18n.messageFormatter('settings.system.deviceUpdatesStateQueued'),
                installingFirmware: i18n.messageFormatter(
                  'settings.system.deviceUpdatesStateInstallingFirmware',
                ),
                abortingUpdate: i18n.messageFormatter(
                  'settings.system.deviceUpdatesStateAbortingUpdate',
                ),
              })}
            </div>
          </div>
        );
      }

      case 'actions': {
        if (props.type === 'in-progress' && firmwareUpdate?.state !== 'error') {
          return progress != null ? (
            <div className={styles.progressBadge}>
              <Glyph
                className={styles.progressSpinnerIcon}
                source={glyph.core.spinner}
                size={theme.icon.size_small}
                color={theme.color.blue_500}
              />
              <span className={styles.progressValue}>{`${progress}%`}</span>
            </div>
          ) : null;
        }

        const actionLabel =
          firmwareUpdate?.state === 'error'
            ? i18n.messageFormatter('settings.system.deviceUpdatesActionRetry')
            : i18n.messageFormatter('settings.system.deviceUpdatesActionUpdate');

        return (
          <>
            <DeviceUpdatesTableButton
              className={styles.updateActionButton}
              isDisabled={isStartingUpdate || scopes[SCOPES.UPDATES] !== true}
              onPress={() => {
                if (checkDeviceReady() === false) {
                  return;
                }

                setPromptOpen(true);
              }}
            >
              {actionLabel}
            </DeviceUpdatesTableButton>

            {isPromptOpen === true && (
              <DeviceUpdatesPromptDialog
                onClose={() => {
                  if (isStartingUpdate === true) {
                    return;
                  }

                  setPromptOpen(false);
                }}
                onStartUpdate={handlePromptStartUpdate}
                isStartingUpdate={isStartingUpdate}
              />
            )}

            {isWakeDialogOpen === true && (
              <DeviceSettingsFirmwareUpdateWakeDialog
                onClose={() => {
                  if (isStartingUpdate === true) {
                    return;
                  }

                  setPendingUpdateVersion(null);
                  setWakeDialogOpen(false);
                }}
                onConfirm={handleWakeConfirm}
                isStartingUpdate={isStartingUpdate}
                wakeInstruction={wakeInstruction}
              />
            )}
          </>
        );
      }

      default:
        return cell.textValue;
    }
  }

  function renderErrorState(type: typeof columnKey) {
    const trimmedFirmwareUpdateMessage =
      firmwareUpdate?.message?.trim() ?? row.value?.firmwareUpdate?.message?.trim();
    const firmwareUpdateErrorMessage =
      trimmedFirmwareUpdateMessage != null && trimmedFirmwareUpdateMessage !== ''
        ? trimmedFirmwareUpdateMessage
        : i18n.messageFormatter('settings.system.deviceUpdatesNoMessage');

    return (
      <div className={styles.nameCell} data-type={type}>
        <Tooltip
          offset={10}
          instant={true}
          anchorPointer={true}
          renderTrigger={(
            triggerRef: Ref<HTMLButtonElement>,
            triggerProps: Record<string, unknown>,
          ) => {
            return (
              <button
                {...triggerProps}
                className={styles.infoTrigger}
                ref={triggerRef}
                type="button"
                aria-label={i18n.messageFormatter(
                  'settings.system.deviceUpdatesStateErrorDetailsAriaLabel',
                )}
              >
                <div className={styles.errorState}>
                  <Glyph
                    size={theme.icon.size_large}
                    source={glyph.core.exclamationmarkTriangleFill}
                    color={theme.color.red}
                  />
                  <span className={clsx(styles.stateLabel, styles.errorStateLabel)}>
                    {i18n.messageFormatter('settings.system.deviceUpdatesStateUpdateFailed')}
                  </span>
                </div>
              </button>
            );
          }}
        >
          <div className={styles.tooltipContent}>{firmwareUpdateErrorMessage}</div>
        </Tooltip>
      </div>
    );
  }

  return (
    <td
      {...mergeProps(tableCell.gridCellProps, focusRing.focusProps)}
      className={clsx(styles.root, styles.tableCell)}
      ref={ref}
    >
      {getContent()}
    </td>
  );
}

function getStateLabel(
  state: string | null,
  message: string | null,
  fallback: string,
  labels: {
    queued: string;
    installingFirmware: string;
    abortingUpdate: string;
  },
) {
  if (message != null && message.trim() !== '') {
    return message;
  }

  switch (state) {
    case 'queued':
      return labels.queued;

    case 'installing':
      return labels.installingFirmware;

    case 'aborting':
      return labels.abortingUpdate;

    default:
      return fallback;
  }
}

function getString(value: string | null | undefined) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function DeviceUpdatesTableButton(props: DeviceUpdatesTableButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const button = useButton(props, ref as RefObject<Element>);
  const focusRing = useFocusRing();

  return (
    <button
      {...mergeProps(button.buttonProps, focusRing.focusProps)}
      className={props.className}
      ref={ref}
      title={props.title}
      type="button"
    >
      {props.children}
    </button>
  );
}
